import { AREA_ID_FOR_WORLD, areaName } from '../game/economy';
import { WORLDS } from '../models/world';
import { type Cutscene } from './cutscene';
import {
  EPILOGUE_CUTSCENE_ID,
  HIDEOUT_CUTSCENE_TIERS,
  hideoutCutsceneId,
  worldCutsceneId,
} from './cutsceneIds';

export interface ChronicleScene {
  readonly id: string;
  readonly title: string;
  readonly seen: boolean;
  readonly hint: string;
}

export interface ChronicleChapter {
  readonly title: string;
  readonly note?: string;
  readonly scenes: readonly ChronicleScene[];
}

export function chronicleChapters(seen: ReadonlySet<string>): readonly ChronicleChapter[] {
  const scene = (id: string, title: string, hint: string): ChronicleScene => ({
    id,
    title,
    seen: seen.has(id),
    hint,
  });

  const worlds = WORLDS.map((world) => ({
    title: `District ${world.id} · ${world.name}`,
    scenes: [
      scene(worldCutsceneId(world.id, 'intro'), 'Arrival', 'Enter the district to witness it.'),
      scene(worldCutsceneId(world.id, 'outro'), 'Aftermath', 'Clear every run in the district.'),
    ],
  }));

  const epilogue = {
    title: 'Epilogue',
    scenes: [scene(EPILOGUE_CUTSCENE_ID, 'The Last Wire', 'Finish all seven fronts.')],
  };

  const hideout = {
    title: 'The Hideout',
    note: "Vignettes from the rebellion's hideout. The hideout is not open in this version yet.",
    scenes: Object.values(AREA_ID_FOR_WORLD).flatMap((areaId) =>
      HIDEOUT_CUTSCENE_TIERS.map((tier) => {
        const numeral = 'I'.repeat(tier);
        return scene(
          hideoutCutsceneId(areaId, tier),
          `${areaName(areaId)} · Stage ${numeral}`,
          `Build the room to stage ${numeral}.`,
        );
      }),
    ),
  };

  return [...worlds, epilogue, hideout];
}

export function chronicleTally(chapters: readonly ChronicleChapter[]): {
  seen: number;
  total: number;
} {
  const scenes = chapters.flatMap((chapter) => chapter.scenes);
  return { seen: scenes.filter((s) => s.seen).length, total: scenes.length };
}

export async function spokenCharacters(
  chapters: readonly ChronicleChapter[],
  load: (cutsceneId: string) => Promise<Cutscene>,
): Promise<ReadonlySet<string>> {
  const seenIds = chapters.flatMap((chapter) =>
    chapter.scenes.filter((s) => s.seen).map((s) => s.id),
  );
  const cutscenes = await Promise.all(seenIds.map((id) => load(id).catch(() => null)));
  const speakers = new Set<string>();
  for (const cutscene of cutscenes) {
    for (const scene of cutscene?.scenes ?? []) speakers.add(scene.speaker.toLowerCase());
  }
  return speakers;
}
